import React, { useState, useEffect } from "react";
import { ScrollView, StyleSheet, View, Pressable } from "react-native";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "@phosphor-icons/react/dist/csr/X";

import { useAppConfig } from "../../../AppConfigProvider";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import {
  useGetCoursesBySchoolByQueryQuery,
  useAddCoursesToDegreeMutation,
  useRemoveCoursesFromDegreeMutation,
  useGetCoursesByDegreeByQueryQuery,
  GetDegreesBySchoolByQueryDocument,
  Degree,
  GetDegreesByDegreeIdDocument,
  GetCoursesByDegreeByQueryDocument,
} from "@axio/graphql/generated/graphql";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import { SearchSelectMulti } from "../../common/form/SearchSelect/SearchSelectMulti";
import NewCustomAxioActionModal from "../../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";

const modals = {
  saveConfirmation: {
    title: "Success!",
    ariaLabel: "Courses Updated Successfully Modal",
    warningText: "Course assignments have been updated successfully.",
    actionButtonText: "OK",
  },
  saveError: {
    title: "Error!",
    ariaLabel: "Courses Update Error Modal",
    warningText: "An error occurred while updating course assignments. Please try again.",
    actionButtonText: "CLOSE",
  },
};

export enum ManageCoursesModalState {
  CLOSED = "CLOSED",
  SAVE_CONFIRMATION = "SAVE_CONFIRMATION",
  SAVE_ERROR = "SAVE_ERROR",
}

const formSchema = z.object({
  courses: z.array(z.object({ id: z.string(), name: z.string() })),
});

type FormData = {
  courses: { id: string; name: string }[];
};

interface ManageCoursesModalProps {
  visible: boolean;
  onClose: () => void;
  degree: Degree;
  refetchDegreeCourses: () => void;
}

const ManageCoursesModal: React.FC<ManageCoursesModalProps> = ({ visible, onClose, degree, refetchDegreeCourses }) => {
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState(ManageCoursesModalState.CLOSED);

  const [searchQueryCourses, setSearchQueryCourses] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const schoolId = appConfig.axioSchools[0];

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      courses: [],
    },
    mode: "onChange",
  });

  const selectedCourses = watch("courses");

  const {
    data: coursesData,
    refetch,
    loading,
  } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId,
      searchQuery: searchQueryCourses,
      page: 1,
      pageSize: 50,
    },
  });

  // Mutations with refetch queries
  const refetchQueries = [
    {
      query: GetDegreesByDegreeIdDocument,
      variables: {
        id: degree.id,
      },
    },
  ];

  const [addCoursesToDegree] = useAddCoursesToDegreeMutation();
  const [removeCoursesFromDegree] = useRemoveCoursesFromDegreeMutation();

  useEffect(() => {
    refetch();
  }, [searchQueryCourses]);

  const handleRemoveCourse = async (courseId: string) => {
    await removeCoursesFromDegree({
      variables: {
        input: {
          degreeId: degree.id,
          courseIds: [courseId],
        },
      },
      refetchQueries,
    });
    await refetchDegreeCourses();
  };

  const handleSubmitForm = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      let success = true;

      // Add courses from form
      if (formData.courses.length > 0) {
        const addResult = await addCoursesToDegree({
          variables: {
            input: {
              degreeId: degree.id,
              courseIds: formData.courses.map((c) => c.id),
            },
          },
          refetchQueries,
        });
        if (!addResult.data?.addCoursesToDegree?.success) {
          success = false;
        }
      }

      await refetchDegreeCourses();

      if (success) {
        setModalState(ManageCoursesModalState.SAVE_CONFIRMATION);
      } else {
        setModalState(ManageCoursesModalState.SAVE_ERROR);
      }
    } catch (error) {
      console.error("Error updating course assignments:", error);
      setModalState(ManageCoursesModalState.SAVE_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    reset();
    onClose();
    setModalState(ManageCoursesModalState.CLOSED);
  };

  const availableCourses = coursesData?.getCoursesBySchoolByQuery?.data.courses ?? [];

  const filteredAvailableCourses = availableCourses.filter(
    (course) => !degree.coursesHasCourse.some((alreadyAssignedCourse) => alreadyAssignedCourse.id === course.id)
  );

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleModalClose}
        title={`Manage Courses for ${degree.name}`}
        ariaLabel="Manage courses modal"
        style={{ width: isMedium ? "60%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}>
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.container}>
            {/* Assigned Courses Section */}
            <View style={styles.section}>
              <CustomText text="Assigned Courses" style={styles.sectionTitle} weight="ultraBold" />
              <CustomText
                text="Courses currently part of this degree program. Click the × to remove a course."
                size="s"
                style={styles.sectionDescription}
              />
              <View style={styles.pillsContainer}>
                {degree.coursesHasCourse.map((course) => (
                  <View key={course.id} style={styles.coursePill}>
                    <CustomText text={course.name} size="s" style={styles.pillText} useTranslationText={false} />
                    <Pressable onPress={() => handleRemoveCourse(course.id)}>
                      <X size={16} color="#1E3DEF" />
                    </Pressable>
                  </View>
                ))}
                {degree.coursesHasCourse.length === 0 && (
                  <CustomText
                    text="No courses assigned to this degree program."
                    size="s"
                    style={styles.noCoursesText}
                  />
                )}
              </View>
            </View>

            {/* Add Courses Section */}
            <View>
              <CustomText text="Add Courses" style={styles.sectionTitle} weight="ultraBold" />
              <CustomText
                text="Search and select courses to add to this degree program."
                size="s"
                style={styles.sectionDescription}
              />

              {/* Search Input */}
              <View>
                <SearchSelectMulti
                  name="courses"
                  control={control}
                  label=""
                  loading={loading}
                  placeholder="Search available courses..."
                  backgroundColor={primaryColor}
                  showAllByDefault={true}
                  itemType="course"
                  fetchOptions={async (searchQuery) => {
                    setSearchQueryCourses(searchQuery);
                    return filteredAvailableCourses.map((course) => ({
                      id: course.id,
                      name: course.name,
                    }));
                  }}
                />
              </View>
            </View>

            {/* Action Buttons */}
            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleModalClose}
                styleType="transparent"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
              />
              <CustomButton
                text="Save Changes"
                onPress={handleSubmit(handleSubmitForm)}
                styleType="primary"
                style={styles.saveButton}
                textStyle={styles.saveButtonText}
                disabled={isSubmitting}
              />
            </View>
          </View>
        </ScrollView>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState === ManageCoursesModalState.SAVE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.saveConfirmation}
      />
      <NewCustomAxioActionModal
        visible={modalState === ManageCoursesModalState.SAVE_ERROR}
        onClose={handleModalClose}
        modalTexts={modals.saveError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#182230",
  },
  sectionDescription: {
    marginBottom: 8,
    color: "#6B7280",
  },
  pillsContainer: {
    padding: 8,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    minHeight: 40,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    maxHeight: 120,
    overflowY: "scroll",
  },
  coursePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#E0ECFF",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  pillText: {
    color: "#374151",
  },
  noCoursesText: {
    color: "#9CA3AF",
    fontStyle: "italic",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  saveButton: {
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  saveButtonText: {
    color: "#FFFFFF",
  },
});

export default ManageCoursesModal;
